import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { PasswordModule } from 'primeng/password';

@Component( {
  selector: 'app-password-reset-dialog',
  templateUrl: './app-password-reset-dialog.component.html',
  styleUrls: [ './app-password-reset-dialog.component.scss' ],
  imports: [CommonModule, ReactiveFormsModule, DialogModule, ButtonModule, PasswordModule],
} )
export class AppPasswordResetDialogComponent implements OnChanges {
  @Input() visible = false;
  /**
   * Value that must be disallowed inside the password (e.g. username or email prefix).
   * If empty, username-disallow validation is skipped.
   */
  @Input() usernameToDisallow = '';

  @Output() confirm = new EventEmitter<string>();

  public form: FormGroup;
  public submitted = false;

  private readonly fb = inject( FormBuilder );

  constructor () {
    this.form = this.fb.group(
      {
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength( 12 ),
            this.containsUsernameValidator.bind( this ),
            this.uppercaseRequiredValidator.bind( this ),
            this.lowercaseRequiredValidator.bind( this ),
            this.digitRequiredValidator.bind( this ),
            this.specialCharRequiredValidator.bind( this ),
          ],
        ],
        passwordConfirmation: [ '', [ Validators.required ] ],
      },
      { validators: this.passwordsMatchValidator },
    );
  }

  ngOnChanges ( changes: SimpleChanges ): void {
    if ( changes['visible']?.currentValue === false ) {
      this.submitted = false;
      this.form.reset( { newPassword: '', passwordConfirmation: '' } );
      return;
    }

    // Re-evaluate field validators that depend on Inputs.
    const usernameChanged = !!changes['usernameToDisallow'];
    if ( usernameChanged ) {
      this.getControl( 'newPassword' )?.updateValueAndValidity();
    }
  }

  public getControl ( fieldName: 'newPassword' | 'passwordConfirmation' ): AbstractControl | null {
    return this.form.get( fieldName );
  }

  public getErrorMessage ( fieldName: 'newPassword' | 'passwordConfirmation' ): string | null {
    if ( fieldName === 'newPassword' ) {
      const errors = this.form.get( 'newPassword' )?.errors;
      if ( !errors ) return null;
      if ( errors['required'] ) return 'New password is required.';
      if ( errors['minlength'] ) return 'Password must be at least 12 characters.';
      if ( errors['containsUsername'] ) return 'Password cannot contain your username.';
      if ( errors['passwordReused'] ) return 'This password was used recently. Please choose a different one.';
      if ( errors['uppercaseMissing'] ) return 'Password must contain at least one uppercase letter.';
      if ( errors['lowercaseMissing'] ) return 'Password must contain at least one lowercase letter.';
      if ( errors['digitMissing'] ) return 'Password must contain at least one digit.';
      if ( errors['specialCharMissing'] ) return 'Password must contain at least one special character.';
      return null;
    }

    const confirmErrors = this.getControl( 'passwordConfirmation' )?.errors;
    if ( confirmErrors?.['required'] ) return 'Password confirmation is required.';
    if ( this.form.errors?.['passwordMismatch'] ) return 'Passwords do not match.';
    return null;
  }

  private passwordsMatchValidator ( group: FormGroup ): ValidationErrors | null {
    const newPassword = group.get( 'newPassword' )?.value as string | undefined;
    const confirmation = group.get( 'passwordConfirmation' )?.value as string | undefined;

    if ( !newPassword || !confirmation ) return null;
    if ( newPassword !== confirmation ) return { passwordMismatch: true };
    return null;
  }

  private containsUsernameValidator ( control: AbstractControl ): ValidationErrors | null {
    const value = control.value as string | undefined;
    const username = this.usernameToDisallow?.trim()?.toLowerCase();
    if ( !value || !username ) return null;
    return value.toLowerCase().includes( username ) ? { containsUsername: true } : null;
  }

  private uppercaseRequiredValidator ( control: AbstractControl ): ValidationErrors | null {
    const value = control.value as string | undefined;
    if ( !value ) return null;
    return /[A-Z]/.test( value ) ? null : { uppercaseMissing: true };
  }

  private lowercaseRequiredValidator ( control: AbstractControl ): ValidationErrors | null {
    const value = control.value as string | undefined;
    if ( !value ) return null;
    return /[a-z]/.test( value ) ? null : { lowercaseMissing: true };
  }

  private digitRequiredValidator ( control: AbstractControl ): ValidationErrors | null {
    const value = control.value as string | undefined;
    if ( !value ) return null;
    return /[0-9]/.test( value ) ? null : { digitMissing: true };
  }

  private specialCharRequiredValidator ( control: AbstractControl ): ValidationErrors | null {
    const value = control.value as string | undefined;
    if ( !value ) return null;
    // Any non-alphanumeric character (punctuation/symbols) counts as a special character.
    return /[^A-Za-z0-9]/.test( value ) ? null : { specialCharMissing: true };
  }

  onSubmit (): void {
    this.submitted = true;
    this.form.markAllAsTouched();

    if ( this.form.invalid ) return;
    this.confirm.emit( this.form.value.newPassword as string );
  }
} 